import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import ActionHeader from "../../../components/ActionHeader";
import usePageState from "../../../hooks/usePageState";
import { useState } from "react";
import { OrderReportResponse } from "../../../../infrastructure/model/OrderModel";
import { FilterRequest } from "../../../../applications/constants/AppTypes";
import {
  DATE_RANGE_OPTION_ENUM,
  GROUP_BY_ENUM,
  GROUP_BY_OPTIONS,
} from "../../../../applications/constants/AppConst";
import useAsyncEffect from "../../../hooks/useAsyncEffect";
import { Button, Col } from "antd";
import Row from "../../../components/Row";
import { useDateRangeSelector } from "../../../hooks/useDateRangeSelector";
import AppInputDropdown from "../../../components/AppInputDropdown";
import { XAxis, YAxis, Legend, Tooltip, LineChart, CartesianGrid, Line } from "recharts";
import AutoSizer from "react-virtualized-auto-sizer";
import { getReportUseCase } from "../../../../applications/useCase/ApiUseCase/OrderApiUseCase";

interface ReportProps {}

function Report(props: ReportProps) {
  const { setLoading, showError } = usePageState();
  const [dataReport, setDataReport] = useState<OrderReportResponse[]>([]);

  const [groupBy, setGroupBy] = useState<GROUP_BY_ENUM>(GROUP_BY_OPTIONS[0].value);

  const { startDate, endDate, DateRangeSelectorComponent, validateDateRange } =
    useDateRangeSelector({
      initSelectOptionDate: DATE_RANGE_OPTION_ENUM.TO_DAY,
    });

  async function getOrderReport() {
    if (!validateDateRange()) return;
    setDataReport([]);

    const res = await getReportUseCase({
      startDate,
      endDate,
      groupBy,
      setLoading,
      showError,
    });
    if (res) {
      setDataReport(res);
    }
  }

  useAsyncEffect(async () => {
    await getOrderReport();
  }, []);

  const getGroupByLabel = (value: GROUP_BY_ENUM): string => {
    return GROUP_BY_OPTIONS.find((opt) => opt.value === value)?.label ?? "";
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #ccc",
            padding: "10px",
          }}
        >
          <p>{label}</p>
          {payload.map((item: any, idx: number) => (
            <p key={idx} style={{ color: item.color }}>
              {item.name === "completed_count" ? "Số đơn hoàn thành" : "Số đơn bị hủy"}:{" "}
              <strong>{item.value}</strong>
            </p>
          ))}
        </div>
      );
    }
  };

  const CustomLegend = ({ payload }: any) => {
    return (
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {payload.map((entry: any, index: number) => (
          <li key={`item-${index}`} style={{ color: entry.color }}>
            ● {entry.value === "completed_count" ? "Số đơn hoàn thành" : "Số đơn bị hủy"}
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="h-100 flex flex-column">
      <ActionHeader
        actions={[
          {
            icon: <ReloadOutlined />,
            isNormal: true,
            userClick: getOrderReport,
            type: "primary",
          },
        ]}
      />
      <Row
        className="gap-2 p-2"
        style={{
          background: "white",
          display: "flex",
          alignItems: "flex-end",
        }}
      >
        <Col span={2}>
          <AppInputDropdown
            orientation="col"
            label="Hiển thị theo"
            options={GROUP_BY_OPTIONS}
            currentValue={groupBy}
            onChangeValue={(e) => {
              setGroupBy(e);
            }}
          />
        </Col>
        <Col span={7}>
          <DateRangeSelectorComponent />
        </Col>
        <Col span={2}>
          <Button
            icon={<SearchOutlined />}
            type="primary"
            onClick={getOrderReport}
            className="w-100"
          >
            Áp dụng
          </Button>
        </Col>
      </Row>
      <div style={{ height: `calc(100% - 60px)`, display: "flex", flexDirection: "column" }}>
        <h3 style={{ textAlign: "left", margin: 4 }}>
          Thống kê đơn hàng theo {getGroupByLabel(groupBy)}
        </h3>
        <div className="h-100 w-100">
          <AutoSizer>
            {({ height, width }) => {
              return (
                <div
                  style={{
                    height: height,
                    width: width,
                  }}
                >
                  <LineChart
                    width={width}
                    height={height}
                    data={dataReport}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip content={CustomTooltip} />
                    <Legend
                      content={CustomLegend}
                      layout="vertical"
                      verticalAlign="top"
                      align="right"
                    />
                    <Line
                      connectNulls
                      type="monotone"
                      dataKey="completed_count"
                      stroke="#8884d8"
                      display={"Số đơn hoàn thành"}
                    />
                    <Line
                      connectNulls
                      type="monotone"
                      dataKey="rejected_count"
                      stroke="#82ca9d"
                      display={"Số đơn bị hủy"}
                    />
                  </LineChart>
                </div>
              );
            }}
          </AutoSizer>
        </div>
      </div>
    </div>
  );
}
export default Report;
